import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  InstallAppBody,
  ListAppReviewsQuery,
  ListMyAppsQuery,
  AppStoreListQuery,
  MyApp,
  MyAppReview,
  RunMyAppBody,
  RunMyAppResult,
  SaveMyAppBody,
  UpsertAppReviewBody,
} from "@/types/(company)/my-app.interface";

// RTK Query slice for the My Space surface of "My App" — same shapes as the
// Company slice (services/(company)/api.my-app.ts), pointed at
// `/employee/my-apps` (Individual-level permission gate on the backend). No
// publish/unpublish/withdraw-submission here: those require the company-level
// `canSettings` grant, which the roles editor hardcodes as company-only, so
// they can never apply to a My Space (Individual-level) role.
export const myAppMySpaceApi = createApi({
  reducerPath: "myAppMySpaceApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "MyApps",
    "MyApp",
    "MyAppAppStore",
    "MyAppReviews",
    "MyAppReview",
  ],
  endpoints: (builder) => ({
    // ── Library (paginated) ───────────────────────────────────────────────
    listMyApps: builder.query<IApiResponse<MyApp[]>, ListMyAppsQuery>({
      query: (q) => ({
        url: "/employee/my-apps",
        method: "GET",
        params: {
          page: q.page,
          limit: q.limit,
          status: q.status,
          search: q.search || undefined,
          sort: q.sort,
          filter: q.filter,
        },
      }),
      providesTags: ["MyApps"],
    }),

    // ── Detail ────────────────────────────────────────────────────────────
    getMyApp: builder.query<IApiResponse<MyApp>, number>({
      query: (id) => ({ url: `/employee/my-apps/${id}`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "MyApp", id }],
    }),

    // ── Save (upsert by company+slug, within the My Space realm) ──────────
    saveMyApp: builder.mutation<IApiResponse<MyApp>, SaveMyAppBody>({
      query: (body) => ({ url: "/employee/my-apps", method: "POST", body }),
      invalidatesTags: (res) => [
        "MyApps",
        ...(res?.data?.id ? [{ type: "MyApp" as const, id: res.data.id }] : []),
      ],
    }),

    // ── Draft (auto-saved as soon as a generation finishes) ───────────────
    saveMyAppDraft: builder.mutation<IApiResponse<MyApp>, SaveMyAppBody>({
      query: (body) => ({
        url: "/employee/my-apps/drafts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MyApps"],
    }),

    // ── Update by id ──────────────────────────────────────────────────────
    updateMyApp: builder.mutation<
      IApiResponse<MyApp>,
      { id: number; body: SaveMyAppBody }
    >({
      query: ({ id, body }) => ({
        url: `/employee/my-apps/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => ["MyApps", { type: "MyApp", id }],
    }),

    // ── Delete ────────────────────────────────────────────────────────────
    deleteMyApp: builder.mutation<IApiResponse<{ deleted: boolean }>, number>({
      query: (id) => ({ url: `/employee/my-apps/${id}`, method: "DELETE" }),
      // Deleting an installed copy (or a published original) must clear the
      // store's "Open" state back to "Install" — the app-store list/detail
      // queries cache `installedAppId` and won't know a copy disappeared
      // unless this tag is invalidated too.
      invalidatesTags: ["MyApps", "MyAppAppStore"],
    }),

    // ── Logo/thumbnail ────────────────────────────────────────────────────
    uploadMyAppLogo: builder.mutation<
      IApiResponse<MyApp>,
      { id: number; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("logo", file);
        return {
          url: `/employee/my-apps/${id}/logo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        "MyApps",
        "MyAppAppStore",
        { type: "MyApp", id },
      ],
    }),
    removeMyAppLogo: builder.mutation<IApiResponse<MyApp>, number>({
      query: (id) => ({
        url: `/employee/my-apps/${id}/logo`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        "MyApps",
        "MyAppAppStore",
        { type: "MyApp", id },
      ],
    }),

    // ── Run (open / refresh / input submit) ───────────────────────────────
    runMyApp: builder.mutation<IApiResponse<RunMyAppResult>, RunMyAppBody>({
      query: (body) => ({ url: "/employee/my-apps/run", method: "POST", body }),
    }),

    // ── App Store (global catalog) ───────────────────────────────────────
    listAppStore: builder.query<IApiResponse<MyApp[]>, AppStoreListQuery>({
      query: (q) => ({
        url: "/employee/my-apps/app-store",
        method: "GET",
        params: {
          page: q.page,
          limit: q.limit,
          search: q.search || undefined,
          module: q.module || undefined,
        },
      }),
      providesTags: ["MyAppAppStore"],
    }),

    listAppStoreModules: builder.query<
      IApiResponse<Array<{ module: string; count: number }>>,
      void
    >({
      query: () => ({
        url: "/employee/my-apps/app-store/modules",
        method: "GET",
      }),
      providesTags: ["MyAppAppStore"],
    }),

    getAppStoreApp: builder.query<IApiResponse<MyApp>, number>({
      query: (id) => ({
        url: `/employee/my-apps/app-store/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "MyAppAppStore", id }],
    }),

    installAppStoreApp: builder.mutation<
      IApiResponse<MyApp>,
      { id: number; body: InstallAppBody }
    >({
      query: ({ id, body }) => ({
        url: `/employee/my-apps/app-store/${id}/install`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MyApps", "MyAppAppStore"],
    }),

    // ── Ratings & reviews on a catalog app ────────────────────────────────
    listAppReviews: builder.query<
      IApiResponse<MyAppReview[]>,
      { id: number } & ListAppReviewsQuery
    >({
      query: ({ id, page, limit }) => ({
        url: `/employee/my-apps/app-store/${id}/reviews`,
        method: "GET",
        params: { page, limit },
      }),
      providesTags: (_r, _e, { id }) => [{ type: "MyAppReviews", id }],
    }),

    getMyAppReview: builder.query<IApiResponse<MyAppReview | null>, number>({
      query: (id) => ({
        url: `/employee/my-apps/app-store/${id}/reviews/mine`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "MyAppReview", id }],
    }),

    upsertAppReview: builder.mutation<
      IApiResponse<MyAppReview>,
      { id: number; body: UpsertAppReviewBody }
    >({
      query: ({ id, body }) => ({
        url: `/employee/my-apps/app-store/${id}/reviews`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "MyAppReviews", id },
        { type: "MyAppReview", id },
        "MyAppAppStore",
      ],
    }),

    deleteAppReview: builder.mutation<
      IApiResponse<{ deleted: boolean }>,
      number
    >({
      query: (id) => ({
        url: `/employee/my-apps/app-store/${id}/reviews`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "MyAppReviews", id },
        { type: "MyAppReview", id },
        "MyAppAppStore",
      ],
    }),
  }),
});

export const {
  useListMyAppsQuery,
  useGetMyAppQuery,
  useSaveMyAppMutation,
  useSaveMyAppDraftMutation,
  useUpdateMyAppMutation,
  useDeleteMyAppMutation,
  useUploadMyAppLogoMutation,
  useRemoveMyAppLogoMutation,
  useRunMyAppMutation,
  useListAppStoreQuery,
  useListAppStoreModulesQuery,
  useGetAppStoreAppQuery,
  useLazyGetAppStoreAppQuery,
  useInstallAppStoreAppMutation,
  useListAppReviewsQuery,
  useGetMyAppReviewQuery,
  useUpsertAppReviewMutation,
  useDeleteAppReviewMutation,
} = myAppMySpaceApi;
